import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";
import { portalHomeFor, errorWords } from "@alm/domain";
import {
  FirstRunSetupForm,
  type FirstRunSetupSubmit,
} from "@/components/app/shell/first-run-setup-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { needsFirstRunSetup } from "@/lib/session/first-run";
import { Building2 } from "@/lib/public-icons";
import { orpc } from "@/lib/orpc";
import { endSession } from "@/lib/session/sign-out";

export const Route = createFileRoute("/setup")({
  ssr: false,
  component: FirstRunSetup,
});

/** The first community of a new organization, before the shell exists. The staff layout sends an
 * administrator with no buildings here and this page sends everyone else back, so neither can
 * strand the other. Full page because the sidebar has nothing to open. */
function leave() {
  void endSession(null);
}

function FirstRunSetup() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useQuery(orpc.auth.me.queryOptions());
  const data = me.data;
  const elsewhere = me.isError
    ? "/login?next=%2Fsetup"
    : !data
      ? null
      : data.user.mustResetPassword
        ? "/set-new-password"
        : (portalHomeFor(data.user.role) ?? (needsFirstRunSetup(data) ? null : "/"));

  useEffect(() => {
    if (elsewhere) void navigate({ href: elsewhere, replace: true });
  }, [elsewhere, navigate]);

  const createFacility = useMutation(orpc.administration.facilities.create.mutationOptions());
  const updateOrganization = useMutation(
    orpc.administration.organizations.updateCurrent.mutationOptions(),
  );
  const pending = createFacility.isPending || updateOrganization.isPending;

  const submit = async ({ acceptsMedicaid, ...facility }: FirstRunSetupSubmit) => {
    try {
      await createFacility.mutateAsync(facility);
      /* The organization starts out accepting Medicaid, so it is only
         written when the operator turned it off. */
      if (!acceptsMedicaid) await updateOrganization.mutateAsync({ acceptsMedicaid });
    } catch (error) {
      toast.error(errorWords(error, "Could not create community"));
      return;
    }
    toast.success("Community created");
    queryClient.clear();
    // A full load, so every provider reads the new building from the start.
    window.location.assign("/");
  };

  if (!data || elsewhere) {
    return (
      <div className="bg-muted/30 min-h-dvh px-4 py-16">
        <div className="mx-auto w-full max-w-2xl">
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-muted/30 min-h-dvh">
      <header className="bg-background border-b">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-3">
          <span className="font-semibold">People AI</span>
          <Button variant="ghost" onClick={leave}>
            Sign out
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-12">
        <div className="mb-8 flex flex-col items-center gap-2 text-center">
          <Building2 className="text-muted-foreground size-8" />
          <h1 className="text-2xl font-semibold tracking-tight text-balance">
            Welcome, {data.user.name}. Let&apos;s set up your first community
          </h1>
          <p className="text-muted-foreground text-sm">
            Add your first facility to start managing residents, staff and care. You can add more
            later.
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Community details</CardTitle>
            <CardDescription>This becomes the first facility in your organization.</CardDescription>
          </CardHeader>
          <CardContent>
            <FirstRunSetupForm onSubmit={(values) => void submit(values)} pending={pending} />
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
